import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Upload, FileImage, X, ArrowLeft, Check, Loader2 } from 'lucide-react';
import axios from 'axios';

const UploadPaymentProof = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId, amount, serviceTitle } = location.state || {};
  
  const [bankAccounts, setBankAccounts] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [formData, setFormData] = useState({
    selectedBank: '',
    transferDate: '',
    transferTime: '',
    transferReference: '',
    additionalNotes: ''
  });
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!orderId) {
      alert('Invalid order. Please start from payment selection.');
      navigate('/');
      return;
    }
    fetchBankAccounts();
  }, []);

  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/bank-accounts`
      );
      setBankAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (selectedFile) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(selectedFile.type)) {
      alert('Invalid file type. Please upload JPG, PNG, or PDF only.');
      return;
    }

    if (selectedFile.size > maxSize) {
      alert('File too large. Maximum size is 5MB.');
      return;
    }

    setFile(selectedFile);

    // Create preview for images
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      alert('Please upload a payment proof file');
      return;
    }

    if (!formData.selectedBank || !formData.transferDate || !formData.transferTime) {
      alert('Please fill in all required fields');
      return;
    }

    setUploading(true);

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('selected_bank', formData.selectedBank);
      
      const transferDateTime = `${formData.transferDate}T${formData.transferTime}:00`;
      uploadFormData.append('transfer_date', transferDateTime);
      uploadFormData.append('transfer_reference', formData.transferReference);
      uploadFormData.append('additional_notes', formData.additionalNotes);

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/bank-transfer/${orderId}/upload-proof`,
        uploadFormData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Navigate to status page
      navigate('/payment/status', {
        state: { orderId, amount, serviceTitle }
      });
    } catch (error) {
      console.error('Error uploading proof:', error);
      alert('Failed to upload payment proof. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Upload Payment Proof</h1>
          <p className="text-gray-600 mt-2">Upload your bank slip or transaction screenshot</p>
        </div>

        {/* Order Info */}
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600">Order ID</p>
                <p className="text-lg font-bold text-blue-600">{orderId}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Amount</p>
                <p className="text-xl font-bold text-gray-900">Rs. {amount?.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit}>
          {/* File Upload */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>📤 Upload Bank Slip / Proof</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all ${
                  dragActive ? 'border-blue-600 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById('fileInput').click()}
              >
                {preview ? (
                  <div className="relative">
                    <img src={preview} alt="Preview" className="max-h-64 mx-auto rounded" />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                        setPreview(null);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : file ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileImage className="h-8 w-8 text-green-600" />
                    <div className="text-left">
                      <p className="font-semibold">{file.name}</p>
                      <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg font-semibold text-gray-700 mb-2">
                      Click to upload or drag and drop
                    </p>
                    <p className="text-sm text-gray-500">
                      Accepted: JPG, PNG, PDF (Max 5MB)
                    </p>
                  </div>
                )}
              </div>
              <input
                id="fileInput"
                type="file"
                className="hidden"
                accept="image/jpeg,image/jpg,image/png,application/pdf"
                onChange={(e) => handleFileChange(e.target.files[0])}
              />
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Payment Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Which bank did you use? *</Label>
                <Select
                  value={formData.selectedBank}
                  onValueChange={(value) => setFormData({ ...formData, selectedBank: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select bank" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((account, index) => (
                      <SelectItem key={index} value={account.bank_name}>
                        {account.bank_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Transfer Date *</Label>
                  <Input
                    type="date"
                    value={formData.transferDate}
                    onChange={(e) => setFormData({ ...formData, transferDate: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div>
                  <Label>Transfer Time *</Label>
                  <Input
                    type="time"
                    value={formData.transferTime}
                    onChange={(e) => setFormData({ ...formData, transferTime: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Transaction Reference (if any)</Label>
                <Input
                  placeholder="TXN123456789"
                  value={formData.transferReference}
                  onChange={(e) => setFormData({ ...formData, transferReference: e.target.value })}
                />
              </div>

              <div>
                <Label>Additional Notes (optional)</Label>
                <Textarea
                  placeholder="I transferred via mobile app..."
                  value={formData.additionalNotes}
                  onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Important Info */}
          <Alert className="mb-6 border-orange-200 bg-orange-50">
            <AlertDescription>
              <p className="font-bold text-orange-900 mb-2">⚠️ IMPORTANT:</p>
              <ul className="space-y-1 text-sm text-orange-800">
                <li>• Upload clear, readable image</li>
                <li>• Ensure amount and date are visible</li>
                <li>• Verification takes 2-24 hours</li>
                <li>• You'll receive email notification once verified</li>
              </ul>
            </AlertDescription>
          </Alert>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-6 text-lg"
            disabled={uploading || !file}
          >
            {uploading ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Check className="h-5 w-5 mr-2" />
                Submit for Verification
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default UploadPaymentProof;